import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { SetQuery, DecodedValueMap } from 'use-query-params'

import { CandidatesQueryParams } from '../../CandidatesContainer.types'

import { CurrentStateLabel, ProgramStateOption } from '@percent/partner-dashboard/services/grants/grantsService.types'

export function useStateFilter({
  validStates,
  queryParams,
  setQueryParams
}: {
  validStates: ProgramStateOption[] | undefined
  queryParams: DecodedValueMap<CandidatesQueryParams>
  setQueryParams: SetQuery<CandidatesQueryParams>
}) {
  const { t } = useTranslation()

  const filterOptions = useMemo(() => {
    const getStateLabel = (state: ProgramStateOption): string => {
      const stateLabels = {
        [CurrentStateLabel.FINALISTS]: t('grants.candidateState.finalists')
      } as const

      return stateLabels[state.label as keyof typeof stateLabels] ?? state.label
    }

    return (
      validStates?.reduce<{ label: string; value: string }[]>((acc, state) => {
        const label = getStateLabel(state)
        const existing = acc.find(option => option.label === label)

        if (existing) {
          existing.value = `${existing.value},${state.key}`
        } else {
          acc.push({ label, value: state.key })
        }

        return acc
      }, []) ?? []
    )
  }, [validStates, t])

  const selectedFilterValues = useMemo(
    () =>
      filterOptions
        .filter(option => option.value.split(',').some(key => queryParams.currentState?.includes(key)))
        .map(option => option.value),
    [filterOptions, queryParams.currentState]
  )

  const handleFilterChange = useCallback(
    (selectedValues: string[]) => {
      const expandedKeys = selectedValues.flatMap(v => v.split(','))
      setQueryParams({ currentState: expandedKeys.length > 0 ? expandedKeys : undefined })
    },
    [setQueryParams]
  )

  return { filterOptions, selectedFilterValues, handleFilterChange }
}
